import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import {
  PhishingCharacteristic,
  PhishingCharacteristicTranslation,
  SyncPhishingCharacteristicItem,
} from '@/client/campaigns';
import {
  usePhishingCharacteristics,
  useUpdatePhishingCharacteristicsList,
} from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

type LocalCharacteristic = {
  rowId: string;
  id?: string;
  enDescription: string;
  daDescription: string;
};

const mapServerToLocal = (
  characteristics: PhishingCharacteristic[],
): LocalCharacteristic[] =>
  characteristics.map((characteristic) => ({
    rowId: characteristic.id,
    id: characteristic.id,
    enDescription:
      characteristic.translations.find((t) => t.language === LanguagesEnum.EN)
        ?.description ?? '',
    daDescription:
      characteristic.translations.find((t) => t.language === LanguagesEnum.DK)
        ?.description ?? '',
  }));

const mapLocalToPayload = (
  characteristics: LocalCharacteristic[],
): SyncPhishingCharacteristicItem[] =>
  characteristics.map((characteristic) => {
    const translations: PhishingCharacteristicTranslation[] = [];
    const en = characteristic.enDescription.trim();
    const da = characteristic.daDescription.trim();

    translations.push({
      language: LanguagesEnum.EN,
      description: en.length > 0 ? en : null,
    });
    translations.push({
      language: LanguagesEnum.DK,
      description: da.length > 0 ? da : null,
    });

    return {
      ...(characteristic.id ? { id: characteristic.id } : {}),
      translations,
    };
  });

export const usePhishingCharacteristicsEditor = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const { phishingCharacteristics, isLoading, refetch } =
    usePhishingCharacteristics();
  const { update, isLoading: isUpdating } =
    useUpdatePhishingCharacteristicsList();

  const [characteristicsCached, setCharacteristicsCached] = useState<
    LocalCharacteristic[]
  >([]);
  const [characteristics, setCharacteristics] = useState<LocalCharacteristic[]>(
    [],
  );
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    const mapped = mapServerToLocal(phishingCharacteristics?.result ?? []);
    setCharacteristics(mapped);
    setCharacteristicsCached(mapped);
  }, [phishingCharacteristics?.result]);

  useEffect(() => {
    setIsChanged(!isEqual(characteristics, characteristicsCached));
  }, [characteristics, characteristicsCached]);

  const isSaving = isUpdating;

  const handleAddRow = (): void => {
    setCharacteristics((current) => [
      ...current,
      { rowId: v4(), enDescription: '', daDescription: '' },
    ]);
  };

  const handleDeleteRow = (rowId: string): void => {
    setCharacteristics((current) =>
      current.filter((characteristic) => characteristic.rowId !== rowId),
    );
  };

  const handleChange = (
    rowId: string,
    field: 'enDescription' | 'daDescription',
    value: string,
  ): void => {
    setCharacteristics((current) =>
      current.map((characteristic) =>
        characteristic.rowId === rowId
          ? { ...characteristic, [field]: value }
          : characteristic,
      ),
    );
  };

  const handleSave = async (): Promise<void> => {
    try {
      await update({ characteristics: mapLocalToPayload(characteristics) });
      toast?.success(
        t('toast.success'),
        t('globalSettings.phishingCharacteristics.saved'),
      );
      await refetch();
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const handleCancel = (): void => {
    setCharacteristics(characteristicsCached);
  };

  return {
    characteristics,
    isLoading,
    isChanged,
    isSaving,
    handleSave,
    handleCancel,
    handleAddRow,
    handleDeleteRow,
    handleChange,
  };
};
